import {useEffect, useState} from "react";
import {useQuery} from "@apollo/client";
import {GarageListQuery, GET_GARAGE_LIST} from "@App/graphql/queries";
import {useSelector} from "react-redux";
import {State} from "@App/state";
import {useDates} from "@App/hooks/useDates";
import {useCalculation} from "@App/hooks/useCalculation";

declare global {
    interface Array<T> {
        min(attribute: string): T | void
        max(attribute: string): T | void
    }
}

Array.prototype.min = function (attribute) {
    return (this.length && this.reduce(function (prev, curr) {
        return prev[attribute] < curr[attribute] ? prev : curr;
    })) || null;
}

Array.prototype.max = function (attribute) {
    return (this.length && this.reduce(function (prev, curr) {
        return prev[attribute] > curr[attribute] ? prev : curr;
    })) || null;
}

/**
 * Hook that alerts clicks outside the passed ref
 */
export const useGarages = ({garageId = null} = {}) => {
    const {formatDate, toIsoString} = useDates()

    const {
        organization,
        service,
        filterBrand,
        brand,
        generation,
        motorization,
        body,
        gearbox,
        mileage,
        initialRegistrationDate,
        subRepairs,
        equipments
    } = useCalculation()

    const place = useSelector((state: State) => state.orders.place)
    const search = useSelector((state: State) => state.filters.search)
    const premiumServices = useSelector((state: State) => state.orders.premiumServices)
    const dates = useSelector((state: State) => state.filters.dates)
    const distance = useSelector((state: State) => state.filters.distance)
    const sortBy = useSelector((state: State) => state.filters.sortBy)
    const bevSupport = useSelector((state: State) => state.filters.bevSupport)
    const onlyBookable = useSelector((state: State) => state.filters.onlyBookable)
    const onlyManufacturer = useSelector((state: State) => state.filters.onlyManufacturer)
    const commercialVehicleSupport = useSelector((state: State) => state.filters.commercialVehicleSupport)
    const garageChains = useSelector((state: State) => state.orders.garage_chain)

    const [fetchMoreLoading, setFetchMoreLoading] = useState(false)

    const [nearestGarage, setNearestGarage] = useState(null)
    const [bestReviewedGarage, setBestReviewedGarage] = useState(null)
    const [earliestAvailableGarage, setEarliestAvailableGarage] = useState(null)
    const [cheapestGarage, setCheapestGarage] = useState(null)
    const [authorizedGarage, setAuthorizedGarage] = useState(null)

    const [offset, setOffset] = useState(0)

    const driverID = useSelector((state: State) => state.auth?.driverData?.uuid)


    useEffect(() => {
        setOffset(0)
    }, [organization, service, brand, place, premiumServices, search, dates, distance, sortBy])

    const [limit] = useState(100)

    const {loading, data, error, refetch, fetchMore} = useQuery<GarageListQuery>(GET_GARAGE_LIST, {
        variables: {
            id: garageId ? garageId : null,
            organization: organization.id,
            lat: place.lat,
            lon: place.lon,
            appointmentsFrom: dates && dates.from ? toIsoString(dates.from) : null,
            appointmentsTo: dates && dates.to ? toIsoString(dates.to) : null,
            distance: distance,
            search: search,
            brand: brand
                ? brand.id
                : filterBrand
                    ? filterBrand.id
                    : null,
            generation: generation ? generation.id : null,
            motorization: motorization ? motorization.id : null,
            body: body ? body.id : null,
            gearbox: gearbox ? gearbox.id : null,
            mileage: mileage,
            withCalculation: organization.settings.garage_list.display_prices ?? false,
            registrationDate: initialRegistrationDate ? formatDate(initialRegistrationDate, 'yyyy-MM-dd') : null,
            services: [service.id],
            sortBy: sortBy,
            limit: limit,
            offset: 0,
            premiumServices: premiumServices && premiumServices.length > 0 ? premiumServices.map((p) => p.id) : null,
            equipments: equipments.map(equipment => equipment.name),
            subRepairs: subRepairs,
            supportsBev: bevSupport,
            onlyBookable: onlyBookable,
            driverID: driverID ? driverID : null,
            ...(onlyManufacturer && {
                garageTypes: [2,4]
            }),
            supportsCommercialVehicles: commercialVehicleSupport,
            ...(garageChains && garageChains.length > 0 && {
                garageChains: garageChains.map((garageChain) => {
                    return garageChain.id
                })
            }),
        },
        skip: !organization || !place || !place.lat || !place.lon,
        onCompleted: ((data) => {
            if (search) {
                setNearestGarage(null)
                setBestReviewedGarage(null)
                setEarliestAvailableGarage(null)
                return
            }

            if (data.organization.garages.data.length <= 0) {
                return
            }

            let garagesForBadges = data.organization.garages.data.filter(garage => garage.pivot.bookable)

            let shouldShowBadges = garagesForBadges.length > 1


            if(shouldShowBadges) {
                setNearestGarage(garagesForBadges.min('distance'))
            } else {
                setNearestGarage(null);
            }

            setBestReviewedGarage(shouldShowBadges ? garagesForBadges.max('rating') : null);

            if(shouldShowBadges) {
                //iterates and reduces through the garages that would be viable for badges, reduces by comparing available booking days to filter out
                // ones that have no available days, then comparing available at to
                // find the one with the earliest available date
                setEarliestAvailableGarage((garagesForBadges.reduce(function (prev, curr) {
                    if (prev.available_days.length > 0 && curr.available_days.length == 0) {
                        return prev
                    }
                    if (prev.available_days.length == 0 && curr.available_days.length > 0) {
                        return curr
                    }
                    if (prev.available_days.length == 0 && curr.available_days.length == 0) {
                        return prev
                    }
                    return prev.available_days[0].available_at <= curr.available_days[0].available_at ? prev : curr;
                })) || null)
            } else {
                setEarliestAvailableGarage(null);
            }


            if (brand) {
                setAuthorizedGarage(garagesForBadges.find(garage => garage.authorizedBrands.find(b => b.id === brand.id)))
            }

            if (organization.settings.garage_list.display_prices) {
                setCheapestGarage(null)
                let cheapestGarage = garagesForBadges.map(garage => ({
                    id: garage.id,
                    price: garage.calculation && garage.calculation.estimated_price ? garage.calculation.estimated_price : 10000
                })).min('price')
                if (cheapestGarage && shouldShowBadges) {
                    // @ts-ignore
                    setCheapestGarage(garagesForBadges.find(garage => garage.id === cheapestGarage.id))
                }
            }
        })
    })

    const loadMore = () => {
        setFetchMoreLoading(true)
        fetchMore({
            variables: {
                offset: offset + limit
            }
        }).then(() => setFetchMoreLoading(false))
        setOffset(offset + limit)
    }

    return {
        loading,
        fetchMoreLoading,
        calculation: data ? data.organization.calculation : null,
        garages: data ? data.organization.garages.data : null,
        paginatorInfo: data ? data.organization.garages.paginatorInfo : null,
        isFiltered: !!search,
        error,
        nearestGarage,
        bestReviewedGarage,
        earliestAvailableGarage,
        cheapestGarage,
        authorizedGarage,
        refetch,
        loadMore
    }
}
